import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Container, Alert } from "react-bootstrap";

const QuickDropzone = () => {
  const navigate = useNavigate();
  const [dragError, setDragError] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  // State for quick drop preference that reacts to localStorage changes
  const [showQuickDrop, setShowQuickDrop] = useState(() => {
    const savedPreference = localStorage.getItem('showQuickDrop');
    return savedPreference === null ? true : JSON.parse(savedPreference);
  });
  
  // Get user email from Redux store
  const { userAuth } = useSelector((state) => state?.users);
  const email = userAuth?.userInfo?.userFound?.email;

  // Listen for localStorage changes (from other tabs or profile page)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedPreference = localStorage.getItem('showQuickDrop');
      if (savedPreference !== null) {
        setShowQuickDrop(JSON.parse(savedPreference));
      }
    };

    // Listen for storage events (changes from other tabs)
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events (changes from same tab)
    window.addEventListener('quickDropToggle', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('quickDropToggle', handleStorageChange);
    };
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    if (!email) {
      toast.error("Please log in to upload files", {
        position: "bottom-right",
        autoClose: 3000
      });
      return;
    }

    if (acceptedFiles.length > 0) {
      // Store the file in a global variable or use a different approach
      const file = acceptedFiles[0];

      // Store file in window object temporarily
      window.quickDropFile = file;

      // Navigate to import page
      navigate('/import');
    }
  }, [email, navigate]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    onDropRejected: (rejectedFiles) => {
      const error = rejectedFiles[0]?.errors[0]?.message || 'Only PDF files are allowed';
      setDragError(error);
      toast.error('Please upload only PDF files', {
        position: "bottom-right",
        autoClose: 3000
      });
      setTimeout(() => setDragError(null), 3000);
    }
  });

  const handleDismiss = () => {
    setIsVisible(false);
    // Store dismissal in localStorage for this session
    localStorage.setItem('quickDropDismissed', 'true');
  };

  // Don't show if dismissed, user preference is disabled, or user is not logged in
  const isDismissed = localStorage.getItem('quickDropDismissed') === 'true';

  if (!showQuickDrop || isDismissed || !isVisible || !email) {
    return null;
  }

  return (
    <Container className="mt-3 mb-4">
      <Alert
        variant="light"
        dismissible
        onClose={handleDismiss}
        className="border-2 border-dashed border-primary bg-light"
        style={{
          borderStyle: 'dashed',
          borderWidth: '2px',
          borderColor: isDragActive ? '#0d6efd' : '#6c757d',
          backgroundColor: isDragActive ? 'rgba(13, 110, 253, 0.1)' : 'rgba(248, 249, 250, 0.8)',
          transition: 'all 0.3s ease'
        }}
      >
        <div
          {...getRootProps()}
          style={{
            cursor: 'pointer',
            padding: '1rem',
            textAlign: 'center',
            borderRadius: '0.5rem',
            transition: 'all 0.3s ease',
            backgroundColor: isDragActive ? 'rgba(13, 110, 253, 0.05)' : 'transparent'
          }}
        >
          <input {...getInputProps()} />
          <div className="d-flex align-items-center justify-content-center">
            {dragError ? (
              <>
                <i className="bi bi-exclamation-triangle text-danger me-2"></i>
                <span className="text-danger fw-medium">
                  {dragError}
                </span>
              </>
            ) : (
              <>
                <i className={`bi bi-cloud-upload me-2 ${
                  isDragActive ? 'text-primary' : 'text-muted'
                }`}></i>
                <span className={`fw-medium ${
                  isDragActive ? 'text-primary' : 'text-muted'
                }`}>
                  {isDragActive ? (
                    "Drop your PDF file here..."
                  ) : (
                    "Quick Upload: Drag and drop PDF file here, or click to select"
                  )}
                </span>
              </>
            )}
          </div>
          {!dragError && (
            <small className="text-muted d-block mt-1">
              Only PDF files are supported • This will take you to the import page
            </small>
          )}
        </div>
      </Alert>
    </Container>
  );
};

export default QuickDropzone;
